// How the Lines and Catalog tabs arrange a quote, in the proposal builder's
// format: lines grouped by family (a dark band) and product group (a
// collapsible row with its subtotal), and the catalog as groups of products
// with the quantity on this quote. Pure, so it can be tested without the page.
import { orderLines } from './lineTree';
import { periodLabel } from './ramp';
import type { BuilderLine, BuilderTotals, CatalogProduct } from './quotesApi';

export const FAMILY_LABELS: Record<string, string> = {
  consulting: 'Consulting',
  studio: 'Studio (services)',
  software: 'Foundation (software)',
  agents: 'AI Agents',
};
const FAMILY_ORDER = ['consulting', 'studio', 'software', 'agents'];

export interface OrderLine {
  id: string;
  name: string;
  sublabel?: string;
  description?: string;
  quantity: number;
  price: string;
  included?: boolean;
  swappable?: boolean;
  /** List, price and discount, for the proposal's investment table. */
  values?: Record<string, string>;
}
export interface OrderGroup {
  key: string;
  label: string;
  total: string;
  totals?: Record<string, string>;
  lines: OrderLine[];
}
export interface OrderSection {
  key: string;
  label: string;
  total: string;
  groups: OrderGroup[];
}

/** A line's price as the order shows it: net, per month for a monthly line. */
const priceOf = (line: BuilderLine, money: (n: number) => string) => `${money(line.net)}${line.billing === 'monthly' ? ' / mo' : ''}`;

/**
 * The quote's lines as order sections: one per family (Other for custom lines),
 * then one group per product group, in catalog order. An option stays under its
 * bundle's group. `swappable` marks lines whose product has alternatives in the catalog.
 */
export function orderSections(
  lines: BuilderLine[],
  catalog: CatalogProduct[],
  money: (n: number) => string,
  { annual = false }: { annual?: boolean } = {},
): OrderSection[] {
  // Price columns for the investment table: monthly lines per month, or per year when `annual`.
  const per = (l: BuilderLine) => (l.billing === 'monthly' ? (annual ? 12 : 1) : 1);
  const unit = (ls: BuilderLine[]) => (ls.length && ls.every((l) => l.billing === 'monthly') ? (annual ? ' / yr' : ' / mo') : '');
  const columns = (ls: BuilderLine[]) => {
    const priced = ls.filter((l) => !l.scope);
    const list = priced.reduce((t, l) => t + l.list * per(l), 0);
    const net = priced.filter((l) => !l.included).reduce((t, l) => t + l.net * per(l), 0);
    const off = list - net;
    return { list: `${money(list)}${unit(ls)}`, price: `${money(net)}${unit(ls)}`, discount: off > 0.005 ? `−${money(off)}` : '' };
  };
  const byId = new Map(lines.map((l) => [l.id, l]));
  const sections = new Map<string, { key: string; label: string; groups: Map<string, { key: string; label: string; sort: number; lines: BuilderLine[] }> }>();
  for (const line of orderLines(lines)) {
    // An option sits with its bundle.
    const anchor = (line.parent_line_id && byId.get(line.parent_line_id)) || line;
    const family = anchor.family ?? 'other';
    const section = sections.get(family) ?? { key: family, label: FAMILY_LABELS[family] ?? 'Other', groups: new Map() };
    sections.set(family, section);
    const groupLabel = anchor.group_name ?? 'Custom lines';
    const groupKey = `${family}:${groupLabel}`;
    const group = section.groups.get(groupKey) ?? { key: groupKey, label: groupLabel, sort: anchor.group_sort ?? 9999, lines: [] };
    section.groups.set(groupKey, group);
    group.lines.push(line);
  }
  const familyRank = (k: string) => (FAMILY_ORDER.includes(k) ? FAMILY_ORDER.indexOf(k) : FAMILY_ORDER.length);
  return [...sections.values()]
    .sort((a, b) => familyRank(a.key) - familyRank(b.key))
    .map((section) => {
      const groups = [...section.groups.values()].sort((a, b) => a.sort - b.sort);
      const sum = (ls: BuilderLine[]) => ls.filter((l) => !l.included && !l.scope).reduce((t, l) => t + l.net, 0);
      const monthly = groups.every((g) => g.lines.every((l) => l.billing === 'monthly'));
      const suffix = monthly ? ' / mo' : '';
      return {
        key: section.key,
        label: section.label,
        total: `${money(sum(groups.flatMap((g) => g.lines)))}${suffix}`,
        groups: groups.map((g) => ({
          key: g.key,
          label: g.label,
          total: `${money(sum(g.lines))}${g.lines.every((l) => l.billing === 'monthly') ? ' / mo' : ''}`,
          totals: columns(g.lines),
          lines: g.lines.map((line) => {
            const parent = line.parent_line_id ? byId.get(line.parent_line_id) : undefined;
            const sublabel = line.included
              ? 'Included'
              : line.scope
                ? `Part of ${parent?.name ?? 'its bundle'}`
                : parent
                  ? `Option of ${parent.name}`
                  : undefined;
            const description = line.customer_note ?? line.product_description ?? undefined;
            return {
              id: line.id,
              name: line.name,
              ...(sublabel ? { sublabel } : {}),
              ...(description ? { description } : {}),
              quantity: line.quantity,
              price: line.scope ? 'Scope' : priceOf(line, money),
              values: line.scope
                ? { list: '', price: 'Scope', discount: '' }
                : line.included
                  ? { ...columns([line]), price: 'Included' }
                  : columns([line]),
              included: line.included,
              // A term line can be ramped; a ramped one shows its periods beneath it.
              rampable: line.billing === 'monthly' && line.selling_model_kind === 'term_defined' && !line.parent_line_id,
              ...(line.ramp_periods?.length
                ? {
                    periods: line.ramp_periods.map((p) => ({
                      key: p.id,
                      label: periodLabel(p, line.quantity_label ?? 'Quantity'),
                      months: p.months,
                      price: `${money(p.net ?? p.list_unit_price)} / mo`,
                    })),
                  }
                : {}),
            };
          }),
        })),
      };
    });
}

const pct = (part: number, whole: number) => (whole ? Math.round((part / whole) * 1000) / 10 : 0);

/** The three stat cards' values and notes: One-time, Monthly, Total discount. */
export function orderStats(totals: BuilderTotals, money: (n: number) => string) {
  const { oneTime, monthly, firstYear } = totals;
  const oneTimeOff = oneTime.lineDiscount + oneTime.discount;
  const monthlyOff = monthly.lineDiscount + monthly.discount;
  const notes = (parts: (string | null)[]) => parts.filter(Boolean).join(' · ');
  return {
    oneTime: {
      value: money(oneTime.net),
      note: notes([
        `${money(oneTime.list + oneTime.included)} list`,
        oneTime.included ? `${money(oneTime.included)} included` : null,
        oneTimeOff ? `${pct(oneTimeOff, oneTime.list)}% off` : null,
      ]),
    },
    monthly: {
      value: money(monthly.after),
      note: notes([`${money(monthly.list)} list`, monthlyOff ? `${pct(monthlyOff, monthly.list)}% off` : null]),
    },
    discount: {
      value: money(firstYear.discount),
      note: [
        oneTime.included ? `${money(oneTime.included)} included` : null,
        oneTimeOff ? `${money(oneTimeOff)} one-time` : null,
        monthlyOff ? `${money(monthlyOff)} / mo × 12` : null,
      ]
        .filter(Boolean)
        .join(' + '),
    },
  };
}

/** The effective discount line under a discount row: "Effective discount 12.8% · −$13,200.00 off list". */
export function effectText(list: number, net: number, money: (n: number) => string): string {
  const off = list - net;
  if (Math.abs(off) < 0.005) return 'At list';
  return off > 0 ? `Effective discount ${pct(off, list)}% · −${money(off)} off list` : `Premium ${pct(-off, list)}% · +${money(-off)} over list`;
}

export interface CatalogGroupView {
  key: string;
  name: string;
  description?: string;
  required: boolean;
  family: string;
  total: string;
  items: {
    id: string;
    name: string;
    description?: string;
    size: 'XS' | 'S' | 'M' | 'L' | 'XL' | null;
    price: string;
    quantity: number;
  }[];
}

/**
 * The catalog as groups for one family, with each product's quantity on the
 * quote's top-level lines and its price (`priceOf`: null when it needs a
 * Company Size first).
 */
export function catalogGroups(
  catalog: CatalogProduct[],
  lines: BuilderLine[],
  family: string,
  priceOf: (product: CatalogProduct) => number | null,
  money: (n: number) => string,
): CatalogGroupView[] {
  const qty = new Map<string, number>();
  for (const l of lines) if (l.product_id && !l.parent_line_id) qty.set(l.product_id, (qty.get(l.product_id) ?? 0) + l.quantity);
  const groups = new Map<string, CatalogGroupView & { sort: number; sum: number }>();
  for (const p of [...catalog].sort((a, b) => a.group_sort - b.group_sort || a.sort_order - b.sort_order)) {
    if (p.family !== family) continue;
    const group = groups.get(p.group_code) ?? {
      key: p.group_code, name: p.group_name, ...(p.group_description ? { description: p.group_description } : {}),
      required: p.group_required, family: p.family, total: '', items: [], sort: p.group_sort, sum: 0,
    };
    groups.set(p.group_code, group);
    const price = priceOf(p);
    const quantity = qty.get(p.id) ?? 0;
    group.sum += (price ?? 0) * quantity;
    group.items.push({
      id: p.id, name: p.name, ...(p.description ? { description: p.description } : {}),
      size: p.size,
      price: price === null ? 'By Company Size' : `${money(price)}${p.billing === 'monthly' ? ' / mo' : ''}`,
      quantity,
    });
  }
  return [...groups.values()].map(({ sort: _sort, sum, ...g }) => ({ ...g, total: money(sum) }));
}
